import { effect } from '@angular/core'
import { firstValueFrom, isObservable } from 'rxjs'
import { StorageStrategy } from './strategies/storage.strategy'

// Inlined rather than imported from @mos/client/shared/utils to avoid a circular
// dependency (client-shared-utils already depends on client-shared-storage).
const isServer = typeof window === 'undefined' || (window as unknown as { isServer?: boolean }).isServer === true

export interface SignalPersistenceOptions<TPersisted extends object> {
	key: string
	storage: StorageStrategy
	/** Reads the slice of state that should be written to storage. Called whenever any signal it reads changes. */
	read(): TPersisted
	/** Applies a value read back from storage onto the store, once, at startup. */
	write(value: TPersisted): void
	/** Transforms a value freshly read from storage before `write` is called, e.g. to revive `Date`s from JSON. */
	revive?(stored: TPersisted): TPersisted
	/** Debounces writes by this many ms, for state that changes rapidly (e.g. on every mousemove). */
	debounceMs?: number
}

/**
 * Native-signals replacement for `@ngneat/elf-persist-state`'s `persistState()`: hydrates a store from
 * `StorageStrategy` once at startup, then keeps storage in sync via an `effect()` on every change.
 * No-ops on the server, same as the previous `if (!isServer)` guards did.
 */
export function signalPersistence<TPersisted extends object>(options: SignalPersistenceOptions<TPersisted>): void {
	if (isServer) return

	hydrate(options)

	let debounceTimeout: ReturnType<typeof setTimeout> | undefined
	effect(() => {
		const value = options.read()
		if (!options.debounceMs) {
			void persist(options.storage, options.key, value)
			return
		}
		clearTimeout(debounceTimeout)
		debounceTimeout = setTimeout(() => void persist(options.storage, options.key, value), options.debounceMs)
	})
}

function hydrate<TPersisted extends object>(options: SignalPersistenceOptions<TPersisted>) {
	const apply = (stored: TPersisted | null | undefined) => {
		if (stored) {
			options.write(options.revive ? options.revive(stored) : stored)
		}
	}

	const result = options.storage.getItem<TPersisted>(options.key)
	// Subscribing directly (rather than `await`ing a Promise/firstValueFrom) matters: a Promise continuation is
	// always deferred to a microtask, even for an already-resolved value, but `Observable.subscribe()` calls its
	// callback synchronously for a synchronously-emitting source (e.g. LocalStorageStrategy's `of(value)`) - the
	// same synchronous-for-localStorage hydration `@ngneat/elf-persist-state` provided, which some consumers
	// (e.g. ResizableDirective, reading the store back on the very next tick) rely on to see the hydrated value
	// before their first read.
	if (isObservable(result)) {
		result.subscribe(apply)
	} else {
		void Promise.resolve(result).then(apply)
	}
}

function persist(storage: StorageStrategy, key: string, value: object) {
	const result = storage.setItem(key, value as Record<string, unknown>)
	return isObservable(result) ? firstValueFrom(result) : result
}
